import React, { useEffect } from "react";
import { FormError } from "../../../components/Error";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { loginRequest, clearLoginSuccess } from "../../../redux/action";
import { toast } from "react-toastify";
import { ArrowBack } from "@mui/icons-material"; // or 'react-icons/md' if using that pack
import { BiEnvelope, BiLockAlt, BiLoaderAlt } from "react-icons/bi";
import { GoBack } from "../../../components/Layout/GoBack";

const Login = () => {
  const history = useHistory();

  const dispatch = useDispatch();

  const { loginSuccess, loginError, loginLoading, user } = useSelector(
    (state) => {
      const {
        success: { login: loginSuccess },
        errors: { login: loginError },
      } = state.ajaxStatuses;

      const { loginLoading } = state.loadingIndicator;

      const { user } = state.userData;

      return {
        loginSuccess,
        loginError,
        loginLoading,
        user,
      };
    },
  );

  const formik = useFormik({
    initialValues: {
      usernameOrEmail: "",
      password: "",
    },
    validationSchema: yup.object({
      usernameOrEmail: yup.string().required("Enter username or email"),
      password: yup.string().required("Enter your password"),
    }),

    onSubmit: (prop) => {
      dispatch(loginRequest(prop));
    },
  });

  useEffect(() => {
    if (loginError) {
      toast.error(loginError, {
        duration: 3000,
      });
    }
  }, [loginError]);

  useEffect(() => {
    if (loginSuccess) {
      // toast.success(loginSuccess, {
      //   duration: 3000,
      // });

      history.push("/dashboard/home");

      dispatch(clearLoginSuccess());
    }
  }, [loginSuccess]);

  return (
    /* 
      Locked to w-[428px] to perfectly replicate the mobile application screen size. 
      Center it on the page with mx-auto if viewing on a desktop monitor.
    */
    <div
      className="w-[428px] mx-auto bg-[#f8faff] min-h-screen relative font-sans text-slate-900 pb-10 shadow-[0_0_50px_rgba(0,0,0,0.1)] overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: "url('/img/new/background/authbg.jpeg')" }}
    >
      <GoBack />

      {/* --- Header --- */}
      <div className="relative z-10 px-8 text-center pb-8">
        <h1 className="text-[28px] font-semibold text-[#071330] mb-3">
          Welcome back
        </h1>
        <p className="text-slate-500 text-sm leading-relaxed px-2">
          Enter your details to access your portfolio.
        </p>
      </div>

      {/* --- Form Card --- */}
      <div className="relative z-10 bg-white mx-4 rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-gray-50">
        <form onSubmit={formik?.handleSubmit} className="flex flex-col gap-5">
          {/* Username or Email */}
          <div>
            <label className="block text-[11px] font-semibold text-[#1a4bdf] tracking-[0.15em] uppercase mb-2 ml-1">
              Username or Email
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1a4bdf]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <input
                type="text"
                {...formik?.getFieldProps("usernameOrEmail")}
                placeholder="username or email@example.com"
                className="w-full bg-white border border-[#dce5fc] rounded-2xl py-4 pl-12 pr-4 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#1a4bdf] transition-colors"
                required
              />
            </div>
            {formik?.errors?.usernameOrEmail &&
              formik?.touched?.usernameOrEmail && (
                <p className="text-red-500 text-xs ml-2 mt-1">
                  {formik.errors.usernameOrEmail}
                </p>
              )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-[11px] font-semibold text-[#1a4bdf] tracking-[0.15em] uppercase mb-2 ml-1">
              Password
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1a4bdf]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    ry="2"
                  ></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </div>
              <input
                type="password"
                {...formik?.getFieldProps("password")}
                placeholder="Enter your password"
                className="w-full bg-white border border-[#dce5fc] rounded-2xl py-4 pl-12 pr-12 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#1a4bdf] transition-colors"
                required
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer hover:text-slate-600">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </div>
            </div>

            {/* Forgot Password & Errors Row */}
            <div className="flex justify-between items-start mt-2 px-1">
              <div>
                {formik?.errors?.password && formik?.touched?.password && (
                  <p className="text-red-500 text-xs font-medium">
                    {formik.errors.password}
                  </p>
                )}
              </div>
              <Link to="/forgot-password">
                <span className="text-xs text-[#1a4bdf] hover:text-blue-700 font-semibold cursor-pointer transition-colors">
                  Forgot password?
                </span>
              </Link>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-4">
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-[#1a4bdf] hover:bg-blue-700 text-white font-medium py-4 rounded-xl flex items-center justify-center relative shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98] disabled:opacity-70"
            >
              {loginLoading ? (
                <div className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span className="text-[17px]">Processing...</span>
                </div>
              ) : (
                <>
                  <span className="text-[17px]">Login</span>
                  <div className="absolute right-4 w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </div>
                </>
              )}
            </button>
          </div>

          {/* Sign Up Link */}
          <div className="text-center mt-4 pb-2">
            <p className="text-[14px] text-slate-500 font-medium">
              New to Africa's Trusted Digital Platform?{" "}
              <Link to="/signup">
                <span className="text-[#1a4bdf] font-semibold cursor-pointer hover:text-blue-700 transition-colors">
                  Create an account
                </span>
              </Link>
            </p>
          </div>
        </form>
      </div>

      {/* --- Footer Security Note --- */}
      <div className="flex items-center justify-center gap-2 mt-8 text-slate-500 text-sm">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#1a4bdf"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          <polyline points="9 12 11 14 15 10"></polyline>
        </svg>
        <span className="font-medium">
          Your data is safe and secure with us
        </span>
      </div>
    </div>
  );
};

export default Login;
